/* iter55f83 — LoginSignInCard.
 *
 * Right-hand sign-in / admin-gateway card. All copy + testids
 * preserved from the pre-refactor LoginPage.
 *
 * iter55g66 — Optional `authError` prop renders a dismissable banner
 * above the Google button when the previous OAuth attempt failed
 * (typically a consumed / expired session_id). Includes a "Try again"
 * hint since the fix is almost always "click the button once more."
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogIn, ShieldCheck, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { P, FONTS, GRAIN_URL } from '@/components/landing/scriptorium/palette';
import { PrivateEmailSignIn } from './PrivateEmailSignIn';

export default function LoginSignInCard({ onGoogleSignIn, onEmailMode, authError = '', onDismissAuthError }) {
  const { t } = useTranslation();
  return (
    <div
      className="relative overflow-hidden rounded-lg border p-4 shadow-lg"
      data-testid="login-signin-card"
      style={{
        backgroundColor: P.paperDeep,
        borderColor:     P.gold,
        boxShadow:       `0 22px 50px -22px ${P.leather}55`,
      }}
    >
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: GRAIN_URL }} />
      <div className="relative text-center">
        <div
          className="text-[10.5px] uppercase tracking-[0.28em]"
          style={{ color: P.oxblood, fontFamily: FONTS.type }}
        >
          {t('login.welcomeBack')}
        </div>
        <h2
          className="mt-1 text-2xl"
          style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}
        >
          {t('login.signInToContinue')}
        </h2>
        <p className="mt-2 text-[12.5px] leading-relaxed" style={{ color: P.inkSoft }}>
          {t('login.signInDescription')}
        </p>

        <div className="mx-auto w-full text-left" data-testid="login-auth-controls">
        {authError && (
          <div
            data-testid="login-auth-error"
            role="alert"
            className="mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-[12px] leading-relaxed"
            style={{
              borderColor:     P.oxblood,
              backgroundColor: `${P.oxblood}11`,
              color:           P.oxblood,
              fontFamily:      FONTS.body,
            }}
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold" style={{ fontFamily: FONTS.display }}>
                {t('login.signInDidntComplete')}
              </div>
              <div className="mt-0.5" style={{ color: P.ink }}>{authError}</div>
            </div>
            {onDismissAuthError && (
              <button
                type="button"
                onClick={onDismissAuthError}
                data-testid="login-auth-error-dismiss"
                aria-label={t('login.dismissError')}
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-black/5"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}

        <Button
          onClick={onGoogleSignIn}
          data-testid="login-google-signin"
          className="mt-5 h-10 w-full gap-2 rounded-full border font-medium text-[12.5px] shadow-sm transition hover:brightness-110 active:scale-[0.98]"
          style={{
            borderColor: P.oxblood,
            color:       P.paper,
            background:  `linear-gradient(180deg, ${P.oxbloodHi} 0%, ${P.oxblood} 100%)`,
            boxShadow:   `0 8px 22px -10px ${P.oxblood}88`,
            fontFamily:  FONTS.display,
          }}
        >
          <GoogleIcon className="h-4 w-4" />
          <span className="font-medium">{authError ? t('login.tryAgainWithGoogle') : t('login.continueWithGoogle')}</span>
        </Button>

        <div
          className="my-3 flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em]"
          style={{ color: P.inkSoft, fontFamily: FONTS.type }}
        >
          <span className="h-px flex-1" style={{ backgroundColor: `${P.gold}66` }} />
          <span>{t('login.orSeparator')}</span>
          <span className="h-px flex-1" style={{ backgroundColor: `${P.gold}66` }} />
        </div>

        <PrivateEmailSignIn
          onForgot={() => onEmailMode('forgot')}
          onCreateAccount={() => onEmailMode('signup')}
        />

        <div
          className="mt-4 rounded-md border p-3 text-[11px] leading-relaxed"
          style={{ borderColor: `${P.gold}88`, backgroundColor: `${P.paper}cc`, color: P.inkSoft }}
        >
          {t('login.responsibleUse')}
        </div>

        <div
          className="mt-5 flex items-center justify-between gap-2 text-[10.5px]"
          style={{ color: P.inkSoft }}
        >
          <span className="inline-flex items-center gap-1.5">
            <LogIn className="h-3 w-3" /> {t('login.firstUserInherits')}
          </span>
          <Link
            to="/admin"
            data-testid="login-admin-gateway"
            className="inline-flex items-center gap-1 rounded-sm border px-2 py-1"
            style={{
              borderColor:     `${P.gold}88`,
              color:           P.leather,
              backgroundColor: P.paper,
              fontFamily:      FONTS.type,
            }}
            title={t('login.admin')}
          >
            <ShieldCheck className="h-3 w-3" /> {t('login.admin')}
          </Link>
        </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path fill="#fff"   d="M21.6 12.227c0-.81-.073-1.589-.21-2.336H12v4.42h5.385c-.232 1.25-.94 2.308-2.005 3.018v2.508h3.245c1.898-1.748 2.975-4.323 2.975-7.61z"/>
      <path fill="#34a853" d="M12 22c2.7 0 4.965-.895 6.62-2.42l-3.245-2.508c-.9.604-2.053.96-3.375.96-2.59 0-4.78-1.748-5.563-4.099H3.085v2.572A9.997 9.997 0 0 0 12 22z"/>
      <path fill="#fbbc05" d="M6.437 13.933A5.99 5.99 0 0 1 6.12 12c0-.67.116-1.32.318-1.933V7.495H3.085A9.997 9.997 0 0 0 2 12c0 1.614.386 3.14 1.085 4.505l3.352-2.572z"/>
      <path fill="#ea4335" d="M12 5.964c1.463 0 2.778.503 3.81 1.49l2.86-2.86C16.96 2.998 14.696 2 12 2A9.997 9.997 0 0 0 3.085 7.495l3.352 2.572C7.22 7.712 9.41 5.964 12 5.964z"/>
    </svg>
  );
}
